import type { AppStore } from "@/store";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import { SubscriptionPlanKind } from "@/store/subscriptions/types";
import type { CommercialPlanKey } from "@/modules/commercial-plans";

import { confirmStripeCheckoutReturn } from "./stripe-checkout-return-confirmation";

export const NON_PROACTIVE_SUBSCRIPTION_RETURN_NOT_CONFIRMED_MESSAGE =
  "We couldn't confirm your upgrade yet. Try again from Billing or contact support.";

export async function handleNonProactiveCheckoutReturn({
  isCommercialCheckout,
  store,
  stripeSessionId,
  commercialCatalogGeneration,
  targetCommercialPlanKey,
}: {
  isCommercialCheckout: boolean;
  store: AppStore;
  stripeSessionId?: string;
  commercialCatalogGeneration?: string;
  targetCommercialPlanKey?: CommercialPlanKey;
}): Promise<boolean> {
  const commercialProperties = {
    ...(commercialCatalogGeneration
      ? { commercial_catalog_generation: commercialCatalogGeneration }
      : {}),
    ...(targetCommercialPlanKey
      ? {
          commercial_plan_key: targetCommercialPlanKey,
          target_commercial_plan_key: targetCommercialPlanKey,
          commercial_upgrade_action: "select_plan" as const,
        }
      : {}),
  };

  try {
    const confirmed = await confirmStripeCheckoutReturn({
      isConfirmed: () => store.subscriptions.billingSubscription?.isPaid === true,
      store,
    });

    if (!confirmed) {
      trackEvent(TrackedEvent.PaywallUpgradeFailed, {
        stripe_session_id: stripeSessionId,
        ...commercialProperties,
        failure_stage: "checkout_return_confirmation",
        failure_reason: "not_confirmed",
      });
      toastModule.triggerToast({
        content: NON_PROACTIVE_SUBSCRIPTION_RETURN_NOT_CONFIRMED_MESSAGE,
      });
      return false;
    }

    if (!isCommercialCheckout) {
      store.platformInfo.setSubscriptionCompletedSuccessfully(SubscriptionPlanKind.PRO);
      store.platformInfo.openWelcomeToProModal();
    }

    return true;
  } catch (unknownError) {
    const error = unknownError as Error;
    logger.error({
      message: "[handleNonProactiveCheckoutReturn] Failed to confirm subscription",
      info: { error: objectModule.safeErrorAsJson(error) },
    });
    trackEvent(TrackedEvent.PaywallUpgradeFailed, {
      stripe_session_id: stripeSessionId,
      ...commercialProperties,
      failure_stage: "checkout_return_confirmation",
      failure_reason: "confirmation_error",
      error_message: error.message,
    });
    toastModule.triggerToast({
      content: NON_PROACTIVE_SUBSCRIPTION_RETURN_NOT_CONFIRMED_MESSAGE,
    });
    return false;
  }
}
